import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Alert, message } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import {
  EVENTS,
  SCHEDULE_EVENT_TYPES,
  SCHEDULE_ANALYTICS_TAB,
  SCHEDULE_REPORT_EXPORT_TYPE,
} from 'shared/constants';
import scheduleApi from 'shared/api/schedule.service.api';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useReportFilters, REPORT_VIEW, reportViewType } from '../../helpers/use-report-filters';
import { useByPersonReport, useByEventReport } from 'shared/hooks/schedule-events.hooks';
import { downloadBlob } from '../../helpers/download-blob';
import { REPORT_EXPORT_FILE } from '../../helpers/report-file-name';
import { buildReportExportedProps } from '../../helpers/report-exported-props';
import { exportErrorMessage } from '../../helpers/event-view.helpers';
import { ReportFilterBar } from './report-filter-bar';
import { ByPersonReport } from './by-person-report';
import { ByEventReport } from './by-event-report';
import { PersonDrillDown } from './person-drill-down';
import { EventDrillDown } from './event-drill-down';
import { ReportExportButton } from './report-export-button';
import styles from './reports.module.scss';

export function ReportsTab() {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const isAdmin = useSelector(isAccountAdmin);

  const {
    view,
    personId,
    eventDetailId,
    from,
    until,
    apiRange,
    setView,
    openPerson,
    openEvent,
    closeDrillDown,
    setRange,
  } = useReportFilters();

  // Intentionally keyed on `view`: each by-person/by-event switch is a distinct
  // page view (mirrors mobile's per-view-toggle PAGE_VIEW). Don't narrow to mount.
  useEffect(() => {
    analyticsService.track(EVENTS.SCHEDULE.PAGE_VIEW, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      tab: SCHEDULE_ANALYTICS_TAB.REPORTS,
      viewType: reportViewType(view),
    });
  }, [view]);

  const filters = { from: apiRange.from, until: apiRange.until };

  const personReport = useByPersonReport(filters);
  const eventReport = useByEventReport(filters);

  if (personId) {
    return (
      <PersonDrillDown
        userId={personId}
        apiRange={apiRange}
        from={from}
        until={until}
        onRangeChange={setRange}
        onSelectEvent={openEvent}
        onBack={closeDrillDown}
      />
    );
  }
  if (eventDetailId) {
    return (
      <EventDrillDown eventId={eventDetailId} onSelectPerson={openPerson} onBack={closeDrillDown} />
    );
  }

  const isPersonView = view === REPORT_VIEW.PERSON;
  const activeQuery = isPersonView ? personReport : eventReport;
  const data = activeQuery.data;
  const rows = data?.items ?? [];
  const total = data?.total ?? rows.length;
  const truncated = total > rows.length;

  const renderReport = () => {
    if (activeQuery.isError) {
      return <Alert type="error" showIcon message={lang.REPORT_LOAD_FAILED} />;
    }
    if (isPersonView) {
      return <ByPersonReport rows={rows} onSelectPerson={openPerson} />;
    }
    return <ByEventReport rows={rows} onSelectEvent={openEvent} />;
  };

  const handleBulkExport = async () => {
    try {
      const { blob } = isPersonView
        ? await scheduleApi.exportPeopleReportBulk({ ...apiRange })
        : await scheduleApi.exportEventsReportBulk({ ...apiRange });
      downloadBlob(blob, `${REPORT_EXPORT_FILE.GENERAL}.xlsx`);
      analyticsService.track(
        EVENTS.SCHEDULE.REPORT_EXPORTED,
        buildReportExportedProps({
          reportType: isPersonView
            ? SCHEDULE_REPORT_EXPORT_TYPE.PEOPLE_BULK
            : SCHEDULE_REPORT_EXPORT_TYPE.EVENTS_BULK,
          viewType: reportViewType(view),
          from: apiRange.from,
          until: apiRange.until,
        }),
      );
    } catch (error) {
      monitorService.error('Failed to export attendance report (bulk)', {
        error,
        view,
        ...apiRange,
      });
      message.error(exportErrorMessage(error, lang));
    }
  };

  return (
    <div className={styles.reportsTab}>
      <ReportFilterBar
        view={view}
        onViewChange={setView}
        from={from}
        until={until}
        onRangeChange={setRange}
        exportSlot={
          isAdmin ? (
            <ReportExportButton
              onExport={handleBulkExport}
              lang={lang}
              disabled={!rows.length || activeQuery.isError}
            />
          ) : null
        }
      />

      {truncated && (
        <Alert
          className={styles.truncationBanner}
          type="warning"
          showIcon
          message={lang.REPORT_TRUNCATED.replace('{count}', formatNumber(rows.length))}
        />
      )}

      {renderReport()}
    </div>
  );
}
